/* @flow */
import React, { memo, useState, useEffect } from 'react';
import { Card, PageHeader, Spin, Typography } from 'antd';
import useWindowSize from '@rehooks/window-size';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { CSVViewer, If, TxtViewer, ImageViewer } from '@/components';
import styles from './DocumentPreview.module.pcss';

type Props = {
  location: {
    state: {
      leadName?: string,
      fileName: string,
      fileUrl: string,
      format: string,
      isFromQuickbase: boolean,
      filePath: string,
    },
  },
  history: {
    goBack: Function,
  },
  fetchDocument?: (filePath: string) => Promise<any>,
  lead: {
    documentUrl: string,
  },
};

const DocumentPreview = ({ location, history, fetchDocument, lead }: Props) => {
  const { documentUrl } = lead;

  let {
    state: { fileUrl },
  } = location;

  const {
    state: { leadName, fileName, format, isFromQuickbase, filePath },
  } = location;

  if (documentUrl !== '') {
    fileUrl = documentUrl;
  }

  const isQuickBase = isFromQuickbase ? true : false;
  useEffect(() => {
    if (isQuickBase == false) {
      fetchDocument(filePath);
    }
  }, []);

  const [numPages, setNumPages] = useState(0);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  let pdfPageWidth = useWindowSize().innerWidth - 320;
  if (pdfPageWidth < 300) pdfPageWidth = 300;

  const pagesCount = Array.from(new Array((numPages: any)), (el, i) => i + 1);
  let title = fileName;
  let extra = null;
  if (format.toUpperCase() === 'PDF' && numPages > 1) {
    title = `Total Pages: ${numPages}`;
    extra = fileName;
  }

  return (
    <div className={styles.root}>
      <PageHeader
        display-if={isFromQuickbase}
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            {leadName || 'Document Preview'}
          </Typography.Title>
        }
        className={styles.header}
      />
      <PageHeader
        display-if={!isFromQuickbase}
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            {leadName || 'N/A'}
          </Typography.Title>
        }
        onBack={() => history.goBack()}
        className={styles.header}
      />
      <Card style={{ marginTop: '24px' }} title={title} bordered={false} extra={extra}>
        <If condition={format.toUpperCase() === 'PDF' && fileUrl !== ''}>
          <Document className={styles.document} loading={<Spin />} file={fileUrl} onLoadSuccess={onDocumentLoadSuccess}>
            {pagesCount.map((page) => (
              <div key={page} className={styles.pageWrapper}>
                <Page
                  className={styles.page}
                  loading={<Spin size="small" />}
                  key={`page_${page}`}
                  pageNumber={page}
                  width={pdfPageWidth}
                />
                <Typography.Paragraph>
                  Page {page} of {numPages}
                </Typography.Paragraph>
              </div>
            ))}
          </Document>
        </If>
        <If condition={format.toUpperCase() === 'CSV' && fileUrl !== ''}>
          <CSVViewer fileUrl={fileUrl} />
        </If>
        <If condition={format.toUpperCase() === 'TXT' && fileUrl !== ''}>
          <TxtViewer fileUrl={fileUrl} />
        </If>
        <If
          condition={
            (format.toUpperCase() === 'JPEG' || format.toUpperCase() === 'JPG' || format.toUpperCase() === 'PNG') &&
            fileUrl !== ''
          }
        >
          <ImageViewer fileUrl={fileUrl} />
        </If>
      </Card>
    </div>
  );
};

const DocumentPreviewMemo = memo<Props>(DocumentPreview);

export default DocumentPreviewMemo;
